// HERO tile for Today view — MIT focus + unified ritual entry points.
// Owns: morning planning button + evening shutdown button (single entry for each).
// today-view.tsx owns modal state; we receive callbacks and review/plan status.
import { MoonStar, Sun } from "lucide-react";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { Task } from "../types";
import { fullDate, PRIO, PRIO_LABEL, Tile, TileHead } from "./bento-ui";
import { todaysPlan, todaysReview } from "./today-rituals-derive";
import { todayIso } from "../lib/date";

const link = "text-[12px] font-bold text-brand-600";

interface Props {
  api: LifeOsApi;
  focusTasks: Task[];
  onGoto: (v: string) => void;
  onMorningPlanning: () => void;
  onEveningShutdown: () => void;
  /** D35: called when user clicks "Tập trung" on an MIT task card. */
  onStartFocus?: (task: Task) => void;
}

export function TodayHero({ api, focusTasks, onGoto, onMorningPlanning, onEveningShutdown, onStartFocus }: Props) {
  const today = todayIso();
  const hasPlan = Boolean(todaysPlan(api.state, today));
  const review = todaysReview(api.state, today);

  return (
    <Tile>
      <TileHead
        title="🎯 Tập trung hôm nay"
        action={
          <div className="flex items-center gap-2">
            {/* Single "Xem việc" link */}
            <button className={link} onClick={() => onGoto("tasks")}>Xem việc</button>
          </div>
        }
      />

      {/* MIT task cards */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {focusTasks.map((t, i) => (
          <div key={t.id} className="rounded-2xl border border-ink-200 p-4">
            <span className="gtext font-display text-3xl font-bold leading-none">{i + 1}</span>
            {/* Chưa khoá kế hoạch → các thẻ chỉ là gợi ý; badge tránh hiểu nhầm đã chốt
                (strip bên dưới nói "Chưa lập kế hoạch" trong khi thẻ đánh số trông như MIT thật) */}
            {!hasPlan && (
              <span className="ml-2 inline-block rounded-full bg-ink-100 px-2 py-0.5 align-[6px] text-[10px] font-bold uppercase tracking-wide text-ink-500">
                Gợi ý
              </span>
            )}
            <p className="mt-2 text-[15px] font-bold leading-snug">{t.title}</p>
            <p className="mt-1 text-[12px] text-ink-500">
              {[fullDate(t.dueDate), `${t.estimateMinutes}m`].join(" · ")}
            </p>
            <div className="mt-3 flex items-center gap-2">
              <span
                className="inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold"
                style={{ background: `${PRIO[t.priority]}1f`, color: PRIO[t.priority] }}
              >
                Ưu tiên {PRIO_LABEL[t.priority]}
              </span>
              {onStartFocus && (
                <button
                  onClick={() => onStartFocus(t)}
                  className="inline-flex items-center gap-1 rounded-xl bg-brand-50 px-2.5 py-0.5 text-[11px] font-bold text-brand-700 hover:bg-brand-100 transition"
                  aria-label={`Bắt đầu tập trung: ${t.title}`}
                >
                  ▶ Tập trung
                </button>
              )}
            </div>
          </div>
        ))}
        {focusTasks.length === 0 && (
          <p className="text-[13px] text-ink-500 col-span-3">
            Chưa có việc nào — bấm <b>Kế hoạch sáng</b> để chọn MIT.
          </p>
        )}
      </div>

      {/* Unified ritual CTA row — one entry point per ritual */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {/* Morning planning */}
        <button
          onClick={onMorningPlanning}
          className={`flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-[12px] font-bold transition ${
            hasPlan
              ? "bg-brand-100 text-brand-600 hover:bg-brand-200"
              : "gbrand text-white"
          }`}
        >
          <Sun className="h-3.5 w-3.5" />
          {hasPlan ? "Sửa kế hoạch sáng" : "Kế hoạch sáng"}
        </button>

        {/* Evening shutdown — show summary if already reviewed, else open button */}
        {review ? (
          <span className="flex items-center gap-1.5 rounded-xl bg-emerald-50 px-3 py-1.5 text-[12px] font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
            <MoonStar className="h-3.5 w-3.5" />
            Đã đóng ngày{review.energy != null ? ` · ${review.energy}/5 năng lượng` : ""}
          </span>
        ) : (
          <button
            onClick={onEveningShutdown}
            className="flex items-center gap-1.5 rounded-xl border border-brand-200 bg-brand-50 px-3 py-1.5 text-[12px] font-bold text-brand-700 hover:bg-brand-100 transition"
          >
            <MoonStar className="h-3.5 w-3.5" />
            Đóng ngày
          </button>
        )}
      </div>
    </Tile>
  );
}
